// dependencies
import React from "react";

import { Link, Text } from "@performyard/py-components";
// components
import { Avatar } from "@performyard/py-components";

// hooks
import { useCompanyFields, useCurrentUser, useIsUserInChainOfCommand } from "../../../../api/hooks";

// schema
import { User } from "../../../../api/services";

import EmployeeURL from "../../../../utils/URLs/Employee";
// utils
import { areFieldsVisible, formatJobTitle } from "./utils";

import AvatarGroup from "../../../AvatarGroup";
import Tooltip from "../../../Tooltip";

type EmployeeProps = {
	employeeData: User;
};

type CanViewFields = {
	phoneNumber: boolean;
	email: boolean;
};

const fieldsToCheckVisibility = [
	{ name: "phoneNumber", lookup: "Phone Number" },
	{ name: "email", lookup: "Email" },
];

const EmployeeCard = (props: EmployeeProps) => {
	const { employeeData } = props;

	const { data: currentUser } = useCurrentUser();
	const userId = currentUser?.me?.authorization?.payload?.user_id;
	const userRole = currentUser?.me?.authorization?.payload?.user_primary_role;
	const companyId = currentUser?.me?.authorization?.payload?.company_id;

	const userIsAdmin = userRole === "admin";
	const userIsBasicAdmin = userRole === "basic-admin";
	const userIsBillingAdmin = userRole === "billing-admin";

	const { data: companyFields } = useCompanyFields(companyId || "");
	const { data: userIsAbove } = useIsUserInChainOfCommand(employeeData?.id, !employeeData?.id);

	const userPermissions = ["all"];

	if (employeeData?.id) {
		if (userId === employeeData.id) {
			userPermissions.push("self");
		}
		if (userIsAdmin || userIsBasicAdmin || userIsBillingAdmin) {
			userPermissions.push("admin");
		}
	}

	if (userIsAbove?.isManager) {
		userPermissions.push("manager");
	}

	let canViewFields: CanViewFields = {
		phoneNumber: false,
		email: false,
	};

	if (companyFields && userPermissions.length) {
		canViewFields = areFieldsVisible(fieldsToCheckVisibility, companyFields, userPermissions) as CanViewFields;
	}

	const hasManagerOrDirectReports = employeeData?.manager || employeeData?.directReportData?.length;

	return (
		<div id="employee-card" className="w-80 rounded-md border border-solid border-neutral-3">
			<div className="box-border flex min-h-14 items-center gap-1 rounded-t-md bg-neutral-0 px-4 py-2">
				<Avatar
					dangerouslySetClassName="flex-shrink-0"
					size={32}
					id={employeeData?.id}
					name={employeeData?.name || "User"}
				/>
				<div className="flex flex-col">
					<Link href={EmployeeURL(employeeData?.id)} dangerouslySetClassName="no-tooltip" forceHardReload>
						<Text
							size="paragraph-2"
							weight="semi-bold"
							color="primary-5"
							dangerouslySetClassName="py-line-clamp-2 max-w-[252px] break-words"
						>
							{employeeData?.name}
						</Text>
					</Link>
					{employeeData?.jobTitle ? (
						<Text
							size="paragraph-3"
							weight="regular"
							color="neutral-7"
							dangerouslySetClassName="py-line-clamp-2 max-w-[252px] break-words"
						>
							{formatJobTitle(employeeData?.jobTitle)}
						</Text>
					) : null}
				</div>
			</div>
			<div
				className={`box-border flex min-h-12 flex-col justify-center bg-neutral-1 px-4 py-2 ${!hasManagerOrDirectReports ? "rounded-b-md" : ""}`}
			>
				{canViewFields.phoneNumber && employeeData?.phoneNumber ? (
					<Text
						size="caption"
						weight="regular"
						color="neutral-7"
						dangerouslySetClassName="py-line-clamp-2 max-w-[252px]"
					>
						{employeeData?.phoneNumber}
					</Text>
				) : null}
				{canViewFields.email ? (
					<Text
						size="caption"
						weight="regular"
						color="neutral-7"
						dangerouslySetClassName="py-line-clamp-2 max-w-[252px]"
					>
						{employeeData?.email}
					</Text>
				) : null}
			</div>
			{hasManagerOrDirectReports ? (
				<div className="box-border flex h-14 flex-row gap-4 rounded-b-md bg-neutral-0 px-4 py-2">
					{employeeData?.manager ? (
						<div
							className={`flex flex-col gap-1 ${employeeData?.directReportData?.length ? "border-r border-solid border-neutral-2 pr-4" : ""}`}
						>
							<span className="text-caption font-semibold">Manager:</span>
							{employeeData?.manager ? (
								<Tooltip id="avatar-tooltip" message={employeeData?.manager?.name} className="w-5">
									<Link className="no-tooltip" href={EmployeeURL(employeeData?.manager?.id)}>
										<Avatar
											size={20}
											id={employeeData?.manager?.id}
											name={employeeData.manager.name}
										/>
									</Link>
								</Tooltip>
							) : null}
						</div>
					) : null}
					{employeeData?.directReportData?.length ? (
						<div className="flex flex-col gap-1">
							<span className="text-caption font-semibold">Direct Reports:</span>

							<AvatarGroup
								hasLinks
								employeeNameTooltip={true}
								employees={employeeData?.directReportData.map((employee) => ({
									_id: employee.id,
									name: employee.name,
								}))}
								maxLength={4}
							/>
						</div>
					) : null}
				</div>
			) : null}
		</div>
	);
};

export default EmployeeCard;
